import { useId } from "react";
import type { ChangeEvent, FC } from "react";
import TextInput from "./TextInput";
import trashIcon from "../../../../../assetsOld/buttonIcons/trash.png";
import { QuestionType, type QuestionTypeValue } from "../../../../../types";
import QuestionTypeSelect from "./QuestionTypeSelect";
import GuestFormAnswerInput from "./GuestFormAnswerInput";
import plusIcon from "../../../../../assetsOld/buttonIcons/plus.png";

interface AppendAnswerProps {
  answer: string;
  position: number;
  questionPosition: number;
}

interface GuestFormAnswerValue {
  answer: string;
  questionPosition: number;
  position: number;
  globalAnswerIndex: number;
}

interface GuestFormQuestionValue {
  question: string;
  type: QuestionTypeValue;
  position: number;
  answers: GuestFormAnswerValue[] | null;
}

interface GuestFormInputProps {
  value?: GuestFormQuestionValue | null;
  placeholder: string;
  onChange: (value: GuestFormQuestionValue) => void;
  onBlur: () => void;
  onRemoveAnswer: (globalAnswerIndex: number) => void;
  onRemoveQuestion: () => void;
  appendAnswer: (newAnswer: AppendAnswerProps) => void;
  questionIndex: number;
}

const GuestFormInput: FC<GuestFormInputProps> = ({
  placeholder,
  value,
  onChange,
  onBlur,
  onRemoveAnswer,
  onRemoveQuestion,
  appendAnswer,
  questionIndex,
}) => {
  const baseId = useId();
  const safeValue: GuestFormQuestionValue = value ?? {
    question: "",
    type: QuestionType.CHECKBOX,
    position: questionIndex,
    answers: [],
  };

  return (
    <div className="mb-5 mt-5 flex flex-col gap-10">
      <div className="flex flex-col gap-[15px]">
        <div key={`${baseId}-question`} className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <span className="font-primary text-400 font-semibold text-grey-400">
              Вопрос {questionIndex + 1}
            </span>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={onRemoveQuestion}
            >
              <img src={trashIcon} alt="Delete" className="h-6 w-6" />
            </button>
          </div>

          <QuestionTypeSelect
            value={safeValue.type}
            onChange={(newType: QuestionTypeValue) => {
              const updatedValue: GuestFormQuestionValue = {
                ...safeValue,
                type: newType,
              };
              onChange(updatedValue);
              onBlur();
            }}
          />

          <TextInput
            placeholder={placeholder}
            label="Введите вопрос"
            labelClassName="text-[12px] font-semibold"
            inputContainerClassName="h-11 rounded-[44px]"
            inputClassName="text-[14px]"
            value={safeValue.question}
            onChange={(e) => {
              const updatedValue: GuestFormQuestionValue = {
                ...safeValue,
                question: e.target.value,
              };
              onChange(updatedValue);
            }}
            onBlur={onBlur}
          />

          {safeValue.type !== QuestionType.TEXT && (
            <div className="flex flex-col gap-3">
              {safeValue.answers?.map(
                (a, answerIndex) =>
                  a.questionPosition === safeValue.position && (
                    <GuestFormAnswerInput
                      index={answerIndex}
                      key={answerIndex}
                      value={a.answer}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const updatedAnswers = (safeValue.answers ?? []).map(
                          (answer, index) => {
                            if (index === answerIndex) {
                              return { ...answer, answer: e.target.value };
                            }
                            return answer;
                          },
                        );
                        onChange({
                          ...safeValue,
                          answers: updatedAnswers,
                        });
                      }}
                      onBlur={onBlur}
                      onRemove={() => onRemoveAnswer(a.globalAnswerIndex)}
                    />
                  ),
              )}

              <button
                type="button"
                className="self-center"
                onClick={() =>
                  appendAnswer({
                    answer: "",
                    position: safeValue.answers?.length || 0,
                    questionPosition: safeValue.position,
                  })
                }
              >
                <div className="mb-1 flex items-center gap-1">
                  <span className="font-primary text-300 font-normal text-grey-400">
                    Добавить ответ
                  </span>
                  <img src={plusIcon} alt="Add" className="h-[15px] w-[15px]" />
                </div>
                <div className="h-[1px] w-full bg-grey-400" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestFormInput;
